// Phase 10B "AutoMind 2050" — Settings.
//
// Dealer, notifications, compliance, integrations, team — grouped into tabs.
// All form sections are restyled with the new ink/gold input styling.
// Existing local state preserved.

import React, { useState } from 'react';
import { Save } from 'lucide-react';

import { PageHeader } from '../components/ui/page-header';
import { TabBar } from '../components/ui/tab-bar';
import { DataCard } from '../components/ui/data-card';
import { StatusPill } from '../components/ui/status-pill';
import SettingsGoogleSheetsPanel from '../components/SettingsGoogleSheetsPanel';

const inputClass =
  'w-full bg-white/60 dark:bg-ink-900/40 border border-ink-200/60 dark:border-ink-800/60 ' +
  'rounded-md px-3 py-2 text-sm focus-2050';
const labelClass = 'block text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1.5';

const Toggle = ({ checked, onChange, ...props }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange?.(!checked)}
    className={
      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors ' +
      (checked ? 'bg-gold' : 'bg-ink-200 dark:bg-ink-800')
    }
    {...props}
  >
    <span
      className={
        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
        (checked ? 'translate-x-[18px]' : 'translate-x-[2px]')
      }
    />
  </button>
);

const Settings = () => {
  const [tab, setTab] = useState('dealer');
  const [callWindowStart, setCallWindowStart] = useState('09:00');
  const [callWindowEnd, setCallWindowEnd] = useState('20:00');
  const [respectDNC, setRespectDNC] = useState(true);
  const [caslCompliance, setCaslCompliance] = useState(true);

  // Phase Audit Fix C: Notifications + Team tabs removed.
  //   - Notifications: provider not wired (see services/alerts/email_adapter.py).
  //   - Team: management already lives under Admin → Users; the empty
  //     pointer tab was clutter.
  const tabs = [
    { id: 'dealer', label: 'Dealer' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'integrations', label: 'Integrations' },
  ];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      <PageHeader
        eyebrow="Workspace"
        title="Settings"
        description="Dealer, compliance, integrations."
        actions={
          // Phase Audit Fix C: no real save handler is wired yet, so
          // the button is disabled with a clarifying tooltip rather
          // than a clickable no-op. Field-level edits (DNC, CASL, the
          // Google Sheets panel) persist through their own handlers;
          // this top-level Save will be wired when the dealer-config
          // bulk update endpoint is finalised.
          <button
            type="button"
            data-testid="save-settings-button"
            className="btn-2050 btn-primary opacity-60 cursor-not-allowed"
            disabled
            title="No changes to save yet — fields persist individually."
            aria-disabled="true"
          >
            <Save className="w-3.5 h-3.5" />
            Save changes
          </button>
        }
      />

      <TabBar value={tab} onChange={setTab} items={tabs} />

      {tab === 'dealer' && (
        <>
          <DataCard
            title="Phone numbers"
            description="Outbound calling numbers."
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 rounded-md border border-ink-200/60 dark:border-ink-800/60">
                <div>
                  <p className="font-mono nums">+1 (438) 798-5050</p>
                  <p className="text-xs text-ink-500 mt-1">Montreal, QC — Primary (Twilio)</p>
                </div>
                <StatusPill tone="success" label="Active" dot />
              </div>
              <button type="button" className="btn-2050 btn-secondary w-full justify-center" disabled>
                Add phone number
              </button>
            </div>
          </DataCard>

          <DataCard
            title="Call windows"
            description="Allowed calling hours (local timezone)."
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Start time</label>
                <input
                  type="time"
                  value={callWindowStart}
                  onChange={(e) => setCallWindowStart(e.target.value)}
                  className={inputClass}
                  data-testid="call-window-start"
                />
              </div>
              <div>
                <label className={labelClass}>End time</label>
                <input
                  type="time"
                  value={callWindowEnd}
                  onChange={(e) => setCallWindowEnd(e.target.value)}
                  className={inputClass}
                  data-testid="call-window-end"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass}>Default timezone</label>
              <select className={inputClass}>
                <option>America/Winnipeg (CST/CDT)</option>
                <option>America/Edmonton (MST/MDT)</option>
                <option>America/Toronto (EST/EDT)</option>
                <option>America/Vancouver (PST/PDT)</option>
              </select>
            </div>
          </DataCard>
        </>
      )}

      {tab === 'compliance' && (
        <DataCard
          title="Compliance"
          description="CASL, DNC, and recording disclosure."
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-md border border-ink-200/60 dark:border-ink-800/60">
              <div className="flex-1">
                <p className="text-sm font-medium">Respect Do Not Call (DNC) list</p>
                <p className="text-xs text-ink-500 mt-1">Automatically skip numbers on the DNC registry.</p>
              </div>
              <Toggle checked={respectDNC} onChange={setRespectDNC} data-testid="dnc-toggle" />
            </div>

            <div className="flex items-center justify-between p-4 rounded-md border border-ink-200/60 dark:border-ink-800/60">
              <div className="flex-1">
                <p className="text-sm font-medium">CASL compliance (Canada)</p>
                <p className="text-xs text-ink-500 mt-1">Enforce consent and opt-out handling.</p>
              </div>
              <Toggle checked={caslCompliance} onChange={setCaslCompliance} data-testid="casl-toggle" />
            </div>

            <div>
              <label className={labelClass}>Recording disclosure</label>
              <textarea
                className={`${inputClass} resize-y`}
                rows={3}
                defaultValue="This call may be recorded for quality assurance purposes."
              />
            </div>
          </div>
        </DataCard>
      )}

      {tab === 'integrations' && (
        <>
          <DataCard
            title="Languages"
            description="Bilingual support."
          >
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-md border border-ink-200/60 dark:border-ink-800/60 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-gold" />
                <div>
                  <p className="text-sm font-medium">English</p>
                  <p className="text-xs text-ink-500">Primary language</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-md border border-ink-200/60 dark:border-ink-800/60 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-gold" />
                <div>
                  <p className="text-sm font-medium">French (Français)</p>
                  <p className="text-xs text-ink-500">Secondary language</p>
                </div>
              </label>
            </div>
          </DataCard>

          <SettingsGoogleSheetsPanel />
        </>
      )}
    </div>
  );
};

export default Settings;
